import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { useGlobalChat } from "@/hooks/use-global-chat";
import { useDocumentIds } from "@/stores/chat-context-store";
import { ChatMessageBubble } from "@/components/documents/chat/ChatMessageBubble";
import { ChatThreadHeader } from "@/components/documents/chat/ChatThreadHeader";
import { ChatQuickActions } from "@/components/documents/chat/ChatQuickActions";
import { ChatPanel } from "./index";
import { GlobalChatInput } from "./GlobalChatInput";
import type { Id } from "@/packages/types";

export function GlobalChatPanel() {
  const { t } = useTranslation(["chat"]);
  const documentIds = useDocumentIds();
  const typedDocumentIds = documentIds as Id<"documents">[];

  const {
    messages,
    sendMessage,
    isStreaming,
    isSending,
    isLoading,
    threads,
    threadId,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
  } = useGlobalChat(typedDocumentIds);

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  const hasMessages = messages.length > 0;

  return (
    <ChatPanel>
      <ChatPanel.Header title={t("assistant.title")} />

      {/* Subtle separator */}
      <div className="h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

      {/* Thread selector header */}
      {threads.length > 0 ? (
        <ChatThreadHeader
          threads={threads}
          activeThreadId={threadId}
          onNewThread={startNewThread}
          onSelectThread={selectThread}
          onDeleteThread={deleteThread}
          onRenameThread={renameThread}
        />
      ) : null}

      {/* Messages area */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            className="flex items-center gap-2.5 text-muted"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="h-4 w-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            <span className="text-[12px] font-medium">
              {t("assistant.loading")}
            </span>
          </motion.div>
        </div>
      ) : !hasMessages ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex flex-col items-center justify-center">
            <ChatQuickActions.EmptyPrompt />
          </div>
          <ChatQuickActions.Suggestions onAction={sendMessage} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 slim-scroll">
          {messages.map((message, i) => (
            <ChatMessageBubble
              key={message.id}
              message={message}
              index={i}
              isStreaming={
                isStreaming &&
                i === messages.length - 1 &&
                message.role === "assistant"
              }
            />
          ))}
          {isStreaming && messages[messages.length - 1]?.role === "user" && (
            <div className="flex items-start">
              <div className="bg-card text-muted rounded-2xl rounded-bl-sm px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>{t("thinking")}</span>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input card with context chips + input */}
      <GlobalChatInput
        placeholder={
          isSending ? t("input.sending") : t("input.mentionPlaceholder")
        }
        onSend={sendMessage}
      />
    </ChatPanel>
  );
}
